import { CalendarClock, Crown, ExternalLink, History, Trophy } from 'lucide-react';
import { useState } from 'react';
import type { MemberClubPayout, MemberClubView } from '../lib/lookup-types';
import { EXPLORER_URL } from '../lib/network';
import { formatDate, formatToken } from '../lib/format';
import { atomicToDecimal } from '../state/AppDataContext';

/*
 * rules/rule13.txt - one member's Leaders Club standing, shown identically in the public lookup,
 * the admin explorer and the member's own panel: which club it is in now (C4 / C8 / C12 / C16),
 * when it entered and left each club, how much club income it has received, and - on click - the
 * settlement-by-settlement detail of that income. Every figure is ON-CHAIN (client 2026-10-09):
 * the API reads the BDynamicLeadersClub events the indexer stored (ClubEntered / ClubSettled /
 * ClubPaid), never the off-chain rules projection.
 */

const CLUB_SLOTS = [4, 8, 12, 16] as const;

/** Extra on-chain fields the API sends (packages/shared MemberClubView); optional for older payloads. */
type ClubCardView = MemberClubView & {
  source?: 'onchain' | 'slots';
  lastSettledAt?: string | null;
  nextSettlementAt?: string | null;
  settlementCount?: number;
  payouts: Array<MemberClubPayout & { txHash?: string }>;
};

const txLink = (hash: string | undefined) => hash
  ? <a href={`${EXPLORER_URL}/tx/${hash}`} target="_blank" rel="noreferrer" className="tx-link" title={hash}>{hash.slice(0, 10)}… <ExternalLink size={11} /></a>
  : <span>—</span>;

export function ClubCard({ club, eyebrow = 'LEADERS CLUB · ON-CHAIN', title = 'Club standing' }: { club: ClubCardView; eyebrow?: string; title?: string }) {
  const [open, setOpen] = useState(false);
  const received = BigInt(club.receivedAtomic);
  const nextDue = club.nextSettlementAt ?? null;
  const nextIsPast = nextDue !== null && Date.parse(nextDue) <= Date.now();

  return (
    <section className="panel club-card">
      <div className="panel-heading">
        <div><span className="eyebrow">{eyebrow}</span><h2><Trophy size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />{title}</h2></div>
        {club.currentClubLabel ? (
          <span className={`club-badge${club.lifetime ? ' lifetime' : ''}`}>{club.lifetime ? <Crown size={12} /> : <Trophy size={12} />} {club.currentClubLabel}{club.lifetime ? ' · lifetime' : ''}</span>
        ) : <span className="club-badge none">Not in a club yet</span>}
      </div>

      <div className="club-track" aria-label="Leaders Club progression">
        {CLUB_SLOTS.map((slot) => {
          const row = club.history.find((item) => item.club === slot);
          const state = row?.current ? 'current' : row ? 'past' : 'locked';
          return (
            <div key={slot} className={`club-step ${state}`}>
              <strong>C{slot}</strong>
              <small>slot {slot}</small>
              <em>
                {state === 'locked'
                  ? 'not reached'
                  : row?.enteredAt
                    ? `in ${formatDate(row.enteredAt)}`
                    : 'entered'}
              </em>
              {row && !row.current ? <em className="exit">out {row.exitedAt ? formatDate(row.exitedAt) : `to C${row.exitedToClub ?? ''}`}</em> : null}
            </div>
          );
        })}
      </div>

      <button type="button" className={`club-income${open ? ' open' : ''}`} onClick={() => setOpen(!open)} aria-expanded={open}>
        <span>Club income received (on-chain)</span>
        <strong>{formatToken(atomicToDecimal(club.receivedAtomic))}</strong>
        <small>{club.payouts.length.toLocaleString()} on-chain payout{club.payouts.length === 1 ? '' : 's'} · click for the detail</small>
      </button>

      <p className="empty-copy club-schedule">
        <CalendarClock size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
        Last distribution: {club.lastSettledAt ? formatDate(club.lastSettledAt) : 'none yet'}
        {' · '}Next distribution: {nextDue ? `${formatDate(nextDue)}${nextIsPast ? ' (due - runs at the next on-chain settlement)' : ''}` : 'not scheduled on-chain yet'}
      </p>

      {open ? (
        <div className="club-income-detail">
          {club.payouts.length === 0 ? (
            <p className="empty-copy">
              No on-chain club settlement has paid this ID yet. The Leaders Club fund in the vault fills from cycle-2 P7 seats and
              the contract distributes it equally inside each club (to IDs with at least 2 directs) at every settlement.
            </p>
          ) : (
            <>
              <div className="ledger-head"><span>Settled</span><span>Club</span><span>Settlement</span><span>Amount</span><span>Tx</span></div>
              {club.payouts.map((payout, index) => (
                <div className="ledger-row" key={`${payout.settlementId}-${payout.club}-${payout.txHash ?? index}`}>
                  <span data-label="Settled"><History size={12} /> {formatDate(payout.settledAt)}</span>
                  <span data-label="Club">C{payout.club}</span>
                  <span data-label="Settlement">#{payout.settlementId}</span>
                  <strong data-label="Amount" className="income-total">{formatToken(atomicToDecimal(payout.amountAtomic))}</strong>
                  <span data-label="Tx">{txLink(payout.txHash)}</span>
                </div>
              ))}
              <div className="route-total"><Trophy size={15} /><span>Total received</span><strong>{formatToken(atomicToDecimal(received.toString()))}</strong></div>
            </>
          )}
        </div>
      ) : null}
      <p className="empty-copy">
        {club.source === 'slots'
          ? 'On-chain: no Leaders Club contract event has been indexed yet, so the club standing is read from this ID\'s activated slots; payouts appear here once the contract settles.'
          : 'On-chain: read from the Leaders Club contract events (club entry, settlements and payouts).'}
      </p>
    </section>
  );
}
